import { nextInteger, normalizeSeed, type LetterWeight } from '../core'

export const BOGGLE_VOWELS = new Set(['A', 'E', 'I', 'O', 'U'])

const DEFAULT_MAX_VOWEL_RATIO_EXCLUSIVE = 0.5
const REFILL_TARGET_PADDING_RATIO = 0.05
const GOLDEN_RATIO_CONJUGATE = 0.6180339887498949

export type BoggleLetterCounts = {
  consonants: number
  vowels: number
}

export type VowelSpawnCountRange = {
  max: number
  min: number
}

export type ClassifiedLetterWeights = {
  consonants: ReadonlyArray<LetterWeight>
  vowels: ReadonlyArray<LetterWeight>
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value))
}

function getMovingTargetRatio(
  minVowelRatio: number,
  maxVowelRatioExclusive: number,
  seed: number | string,
  step: number,
) {
  const bandWidth = Math.max(0, maxVowelRatioExclusive - minVowelRatio)
  const padding = Math.min(REFILL_TARGET_PADDING_RATIO, bandWidth / 4)
  const interiorMin = minVowelRatio + padding
  const interiorMax = maxVowelRatioExclusive - padding

  if (interiorMax <= interiorMin) {
    return minVowelRatio + bandWidth / 2
  }

  const seedOffset =
    normalizeSeed(`${seed}:boggle-refill-target`) / 0x1_0000_0000
  const phase = (seedOffset + step * GOLDEN_RATIO_CONJUGATE) % 1
  const triangle = phase <= 0.5 ? phase * 2 : (1 - phase) * 2

  return interiorMin + triangle * (interiorMax - interiorMin)
}

export function partitionLetterWeights(
  weights: ReadonlyArray<LetterWeight>,
  vowels: ReadonlySet<string> = BOGGLE_VOWELS,
): ClassifiedLetterWeights {
  const vowelWeights: LetterWeight[] = []
  const consonantWeights: LetterWeight[] = []

  for (const weight of weights) {
    if (vowels.has(weight[0])) {
      vowelWeights.push(weight)
    } else {
      consonantWeights.push(weight)
    }
  }

  return {
    consonants: consonantWeights,
    vowels: vowelWeights,
  }
}

export function getFeasibleVowelSpawnCountRange(
  counts: BoggleLetterCounts,
  spawnCount: number,
  minVowelRatio: number,
  maxVowelRatioExclusive = DEFAULT_MAX_VOWEL_RATIO_EXCLUSIVE,
): VowelSpawnCountRange {
  const totalLetters = counts.consonants + counts.vowels + spawnCount
  const min = Math.max(
    0,
    Math.ceil(totalLetters * minVowelRatio - counts.vowels),
  )
  const maxFromRatio =
    maxVowelRatioExclusive === DEFAULT_MAX_VOWEL_RATIO_EXCLUSIVE
      ? Math.floor((counts.consonants + spawnCount - counts.vowels - 1) / 2)
      : Math.floor(totalLetters * maxVowelRatioExclusive - 1e-9 - counts.vowels)
  const max = Math.min(spawnCount, maxFromRatio)

  return { max, min }
}

export function pickVowelSpawnCount(
  counts: BoggleLetterCounts,
  spawnCount: number,
  {
    maxVowelRatioExclusive = DEFAULT_MAX_VOWEL_RATIO_EXCLUSIVE,
    minVowelRatio,
    rngState,
    seed,
    step,
  }: {
    maxVowelRatioExclusive?: number
    minVowelRatio: number
    rngState: number
    seed: number | string
    step: number
  },
) {
  const feasibleRange = getFeasibleVowelSpawnCountRange(
    counts,
    spawnCount,
    minVowelRatio,
    maxVowelRatioExclusive,
  )
  const totalLetters = counts.consonants + counts.vowels + spawnCount
  const targetRatio = getMovingTargetRatio(
    minVowelRatio,
    maxVowelRatioExclusive,
    seed,
    step,
  )
  const centeredTarget = Math.round(totalLetters * targetRatio - counts.vowels)
  const jitterRoll = nextInteger(rngState, 3)
  const jitteredTarget = centeredTarget + jitterRoll.value - 1

  if (feasibleRange.min <= feasibleRange.max) {
    return {
      feasibleRange,
      rngState: jitterRoll.rngState,
      targetRatio,
      vowelSpawnCount: clamp(
        jitteredTarget,
        feasibleRange.min,
        feasibleRange.max,
      ),
    }
  }

  const lowerCandidate = clamp(feasibleRange.max, 0, spawnCount)
  const upperCandidate = clamp(feasibleRange.min, 0, spawnCount)
  const lowerDistance = Math.abs(jitteredTarget - lowerCandidate)
  const upperDistance = Math.abs(jitteredTarget - upperCandidate)

  return {
    feasibleRange,
    rngState: jitterRoll.rngState,
    targetRatio,
    vowelSpawnCount:
      lowerDistance <= upperDistance ? lowerCandidate : upperCandidate,
  }
}
